/* ==========================================================================
   API Client
   Axios instance with JWT handling and error transformation
   Task F26: Enhanced error handling with network error detection
   ========================================================================== */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { ApiError } from './types'
import { sessionExpiredEvent } from '@/utils/sessionEvents'
import { keyFor } from '@/utils/portalSlug'
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/utils/safeStorage'

/* --------------------------------------------------------------------------
   Configuration
   -------------------------------------------------------------------------- */

const BASE_URL = import.meta.env.VITE_API_URL || '/api'
const TOKEN_STORAGE_KEY = 'tjm_access_token'

/* --------------------------------------------------------------------------
   Token Management

   Tokens are stored per pharmacy portal slug (`tjm_access_token:<slug>`) so a
   user can be logged into multiple portals at once. Each helper defaults to the
   slug in the current URL; pass an explicit slug for no-slug handoff flows.
   -------------------------------------------------------------------------- */

export function getStoredToken(slug?: string | null): string | null {
  return safeGetItem(keyFor(TOKEN_STORAGE_KEY, slug))
}

export function setStoredToken(token: string, slug?: string | null): void {
  safeSetItem(keyFor(TOKEN_STORAGE_KEY, slug), token)
}

export function clearStoredToken(slug?: string | null): void {
  safeRemoveItem(keyFor(TOKEN_STORAGE_KEY, slug))
}

/* --------------------------------------------------------------------------
   Error Handling
   -------------------------------------------------------------------------- */

export class ApiClientError extends Error {
  public readonly status: number
  public readonly data: ApiError | null
  public readonly isValidationError: boolean
  public readonly isNetworkError: boolean
  public readonly isTimeoutError: boolean
  public readonly isServerError: boolean

  constructor(
    message: string,
    status: number,
    data: ApiError | null = null,
    options: {
      isValidationError?: boolean
      isNetworkError?: boolean
      isTimeoutError?: boolean
    } = {}
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.data = data
    this.isValidationError = options.isValidationError ?? false
    this.isNetworkError = options.isNetworkError ?? false
    this.isTimeoutError = options.isTimeoutError ?? false
    this.isServerError = status >= 500
  }

  /**
   * Get a user-friendly error message.
   * Note: The message is already extracted from the API response during construction,
   * so this method simply returns this.message for consistency.
   */
  getUserMessage(): string {
    return this.message
  }

  /**
   * Check if this error is retryable (network issues, server errors).
   */
  isRetryable(): boolean {
    return this.isNetworkError || this.isTimeoutError || this.isServerError
  }
}

/**
 * Extract user-friendly message from API error response.
 */
function extractApiMessage(data: ApiError | null): string | null {
  if (!data) return null

  // Handle string detail (simple error message from API)
  if (typeof data.detail === 'string') {
    return data.detail
  }

  // Handle validation error array
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    return data.detail.map((err) => err.msg).join(', ')
  }

  return null
}

/**
 * Check if the error is a network-related error (no response received).
 */
function isNetworkError(error: AxiosError): boolean {
  // No response means network error or CORS issue
  if (!error.response) {
    // Check for specific axios error codes
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      return true
    }
    // Check for network error message
    if (error.message === 'Network Error') {
      return true
    }
    return true
  }
  return false
}

/**
 * Check if the error is a timeout error.
 */
function isTimeoutError(error: AxiosError): boolean {
  return error.code === 'ECONNABORTED' && error.message.includes('timeout')
}

/**
 * Transform axios error into consistent ApiClientError.
 * Prioritizes the actual API error message over generic fallbacks.
 * Task F26: Enhanced with network error detection.
 */
function transformError(error: AxiosError<ApiError>): ApiClientError {
  const status = error.response?.status || 0
  const data = error.response?.data || null

  // Determine error types
  const networkError = isNetworkError(error)
  const timeoutError = isTimeoutError(error)
  const validationError = status === 422

  // First, try to get the actual error message from the API response
  const apiMessage = extractApiMessage(data)

  // Fall back to user-friendly messages based on error type
  let message: string
  if (apiMessage) {
    message = apiMessage
  } else if (timeoutError) {
    message = 'Request timed out. Please check your connection and try again.'
  } else if (networkError) {
    message = 'Unable to connect to the server. Please check your internet connection and try again.'
  } else {
    switch (status) {
      case 400:
        message = 'The request was invalid. Please check your input.'
        break
      case 401:
        message = 'Your session has expired. Please log in again.'
        break
      case 403:
        message = 'You do not have permission to perform this action.'
        break
      case 404:
        message = 'The requested resource was not found.'
        break
      case 422:
        message = 'Please check your input and try again.'
        break
      case 429:
        message = 'Too many requests. Please wait a moment and try again.'
        break
      case 500:
        message = 'An unexpected server error occurred. Please try again later.'
        break
      case 502:
        message = 'The server is temporarily unavailable. Please try again later.'
        break
      case 503:
        message = 'The service is temporarily unavailable. Please try again later.'
        break
      case 504:
        message = 'The server took too long to respond. Please try again.'
        break
      default:
        message = error.message || 'An unexpected error occurred. Please try again.'
    }
  }

  return new ApiClientError(message, status, data, {
    isValidationError: validationError,
    isNetworkError: networkError,
    isTimeoutError: timeoutError,
  })
}

/* --------------------------------------------------------------------------
   Axios Instance
   -------------------------------------------------------------------------- */

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
})

/* --------------------------------------------------------------------------
   Request Interceptor
   Attaches JWT token to Authorization header
   -------------------------------------------------------------------------- */

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/* --------------------------------------------------------------------------
   Response Interceptor
   Handles 401 (session expiry) and error transformation
   Task F26: Now dispatches session expired event instead of hard redirect
   -------------------------------------------------------------------------- */

// Track if we've already handled session expiry to prevent multiple events
let hasDispatchedSessionExpired = false

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError<ApiError>) => {
    const currentPath = window.location.pathname

    // Check if we're on an auth page (login, password reset, etc.)
    const isAuthPage =
      currentPath.includes('/login') ||
      currentPath.includes('/set-password') ||
      currentPath.includes('/accept-invitation') ||
      currentPath.includes('/reset-password') ||
      currentPath.includes('/forgot-password')

    // Handle 401 Unauthorized - dispatch session expired event (but not on auth pages)
    if (
      error.response?.status === 401 &&
      !hasDispatchedSessionExpired &&
      !isAuthPage
    ) {
      hasDispatchedSessionExpired = true
      clearStoredToken()

      // Dispatch session expired event so the modal can show
      // Use setTimeout to allow current operation to complete
      setTimeout(() => {
        sessionExpiredEvent.dispatch()
        // Reset flag after a short delay to allow re-triggering if needed
        setTimeout(() => {
          hasDispatchedSessionExpired = false
        }, 1000)
      }, 0)
    }

    // Transform error for consistent handling
    const apiError = transformError(error)
    return Promise.reject(apiError)
  }
)

/* --------------------------------------------------------------------------
   Exports
   -------------------------------------------------------------------------- */

export default apiClient
export { apiClient }
